import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Icon, Input, Dropdown } from 'semantic-ui-react'
import Layout from '~/components/Layout'
import { FormFields } from '~/components/FormFields'
import { Controller, useForm } from 'react-hook-form'
import FileUpload from '~/components/FileUpload'
import { useAuth } from '~/routes/components/AuthProvider'
import notify from '~/components/Notify'
import { useNavigate, useParams } from 'react-router-dom'
import { UsersQuery } from '~/QueryHooks/UsersQuery'
import UsersService from '~/api/services/users'
import { StyledImageContainer, StyledImageWrapper } from '~/pages/Users/User/Edit/style'
import { RouteTypes } from '~/routes/constants'
import { emailRegex } from '~/components/FormFields/constants'
import { FARMER_ROLE_SLUG, MANAGER_ROLE_SLUG } from '~/Constants'
import { handleErrors, parseDate } from '~/utils/helpers'
import DairyQuery from '~/QueryHooks/DairyQuery'
import { DateInput } from 'semantic-ui-calendar-react'
import dayjs from 'dayjs'

const defaultImage = '/assets/default.png'

const CreateManagerUser: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { systemData, systemData: { languages, logged: { languageId } } } = useAuth()
  const { id } = useParams()
  const managerRole = systemData?.roles.find(role => role.slug === MANAGER_ROLE_SLUG)
  const languageLocal = languages.find(language => language.id === languageId)

  const [correctEmail, setCorrectEmail] = useState(true)
  const [isBusy, setIsBusy] = useState(false)

  const {
    isLoading: isBusyOptions,
    data: usersOptions,
    refetch: fetchUserOptions
  } = UsersQuery.getUserOptionsData()

  const {
    data: countries,
    isLoading: isLoadingCountries,
    refetch: fetchCountries
  } = DairyQuery.getCountriesList()

  const { handleSubmit, control, setValue, watch } = useForm({
    mode: 'onSubmit'
  })

  const photoFieldUrl = watch('photo')
  const wathcMobileCodeField = watch('mobilePrefix')
  const mobileNumber = watch('mobileNumber')

  useEffect(() => {
    fetchUserOptions()
    fetchCountries()
  }, [t])

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      if (data?.birthDate) {
        data.birthDate = parseDate(data.birthDate)
      }
      await UsersService.storeUser({ ...data, managerId: id, roleId: managerRole.id })
      notify(t('Successfully created user'), 'success')
      navigate(RouteTypes.DAIRY_FACTORY_MANAGER_USERS)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderProfile() {
    const maxDate = dayjs().subtract(18, 'year').format('DD.MM.YYYY')
    const minDate = dayjs().subtract(90, 'year').format('DD.MM.YYYY')
    return <>
      <Form.Field>
        {photoFieldUrl && <StyledImageWrapper>
          <img src={photoFieldUrl ? photoFieldUrl : defaultImage}></img>
          <StyledImageContainer />
          {photoFieldUrl && <Icon
            circular
            onClick={(e) => { e.preventDefault(); setValue('photo', null) }}
            name='trash'
            className='trashCan'
          />
          }
        </StyledImageWrapper>
        }
        {!photoFieldUrl && <FileUpload
          encodeFiles
          maxWidthDropZone={'100%'}
          onChange={(value) => setValue('photo', value)}
          accept={{
            'image/bmp': ['.bmp'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png']
          }}
          sizeLimitInMB={5}
        />
        }
      </Form.Field>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='users' style={{ fontSize: '1.2em' }} />
            <div>{t('First name')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter first name')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'firstName'}
          placeholder={t('First name')}
        />
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='users' style={{ fontSize: '1.2em' }} />
            <div>{t('Last name')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter last name')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'lastName'}
          placeholder={t('Last name')}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='at' style={{ fontSize: '1.2em' }} />
            <div>{t('Email')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter email')
            },
            pattern: {
              value: emailRegex,
              message: t('Please enter valid email')
            }
          }}
          typeInput={'emailField'}
          handleEmailCheck={(correctEmail) => setCorrectEmail(correctEmail)}
          style={{ marginBottom: 10 }}
          name={'email'}
          placeholder={t('Email')}
        />
        {languages.length > 1 && <FormFields.Select
          options={systemData?.languages.map(language => ({ value: language.id, text: language.label }))}
          selection
          selectOnBlur={false}
          placeholder={t('Select Language')}
          rules={{
            required: {
              value: true,
              message: t('Please select language')
            }
          }}
          control={control}
          style={{ marginBottom: 10 }}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'world'} style={{ fontSize: '1.2em' }} />
            <div>{t('Language')}</div>
          </label>}
          name={'languageId'}
        />}
      </Form.Group>
      <Form.Group widths={'equal'}>
        <Form.Field style={{ paddingLeft: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'mobile alternate'} style={{ fontSize: '1.2em' }} />
            <div>{t('Mobile number')}</div>
          </label>
          <Input
            style={{ minWidth: 250 }}
            value={mobileNumber}
            label={<Dropdown
              scrolling
              basic
              clearable
              options={countries?.map(countryCode => ({ value: countryCode.id, text: countryCode.phoneCode, flag: countryCode.iso }))}
              selectOnBlur={false}
              onChange={(e, data) => setValue('mobilePrefix', data.value)}
              value={parseInt(wathcMobileCodeField) || ''}
              placeholder={t('Code')}
              name={'mobilePrefix'}
            />}
            onChange={(e, data) => setValue('mobileNumber', data.value)}
            labelPosition='left'
            placeholder={wathcMobileCodeField ? t('Mobile number') : t('Please select mobile code')}
            type={'number'}
            name={'mobileNumber'}
          />
        </Form.Field>
        <Controller
          control={control}
          name='birthDate'
          rules={{
            validate: (value) => {
              if (!value) {
                return true
              }

              const age = dayjs().diff(dayjs(value, 'DD.MM.YYYY'), 'year')
              if (age < 18) return t('You must be at least 18 years old')
              if (age > 90) return t('Date of birth is not valid')
              return true
            }
          }}
          render={({ field, fieldState }) => (
            <Form.Field error={Boolean(fieldState.error)}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 3 }}>
                <Icon name={'calendar alternate outline'} style={{ fontSize: '1.2em' }} />
                <div>{t('Date of birth')}</div>
              </label>
              <DateInput
                name='birthDate'
                placeholder={t('Date')}
                value={field.value || ''}
                dateFormat='DD.MM.YYYY'
                clearable
                iconPosition='left'
                localization={languageLocal.code}
                closable
                minDate={minDate}
                maxDate={maxDate}
                initialDate={maxDate}
                autoComplete='new-password'
                onChange={(e, { value }) => field.onChange(value)}
              />
            </Form.Field>
          )}
        />

      </Form.Group>
    </>
  }

  const role = usersOptions?.roles.find(role => role.slug === FARMER_ROLE_SLUG)?.label

  return <Layout
    segmented
    onSubmit={handleSubmit(onSubmit)}
    isBusy={isBusy || isBusyOptions || isLoadingCountries}
    disableSubmit={!correctEmail}
    action={t('Add')}
    title={t('{{role}} add', { role })}
  >
    {renderProfile()}
  </Layout>
}
export default CreateManagerUser
